import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowLeft, Wrench } from 'lucide-react';
import { ModalPageView } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { useFuzzySearch } from '@cotera/client/app/hooks/use-fuzzy-search';
import { Tool } from '@cotera/client/app/hooks/use-tools';
import {
  useAvailableTools,
  AvailableTool,
} from '@cotera/client/app/hooks/use-available-tools';
import { Loading } from '@cotera/client/app/components';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';
import { FilterMode, USE_CASES, POPULAR_TOOL_KEYS } from './constants';
import { getProviderFromToolKey } from './utils';
import {
  buildCatalog,
  CatalogRow,
  PROVIDER_SEARCH_KEYS,
  TOOL_SEARCH_KEYS,
  toProviderSearchRecord,
  toToolSearchRecord,
} from './catalog-model';
import { CatalogList } from './catalog-list';
import { ProviderDetailPanel } from './provider-detail';
import { CatalogHeader, CatalogEmptyState } from './ui';

/** Which provider (and optionally which of its tools) the right pane shows. */
type Selection = { providerKey: string; toolKey?: string };

const selectionFor = (row: CatalogRow): Selection =>
  row.t === 'provider'
    ? { providerKey: row.provider.provider }
    : { providerKey: row.provider.provider, toolKey: row.tool.key };

const rowIdFor = (selection: Selection | null): string | null => {
  if (selection === null) {
    return null;
  }
  return selection.toolKey !== undefined
    ? `tool:${selection.toolKey}`
    : `provider:${selection.providerKey}`;
};

/**
 * Two-pane tool browser: a searchable, keyboard-navigable catalog on the left
 * and the selected provider's connection + tools on the right.
 *
 * The panes replace what used to be a stack of pushed modal pages, so drilling
 * into a provider no longer costs you the list you were reading.
 */
export const ToolConnections: React.FC<{
  onSelect: (tool: Tool & { isExisting?: boolean }) => Promise<void>;
  className?: string;
  toolsClassName?: string;
  /** Opens straight onto this tool's provider, with the tool expanded. */
  initialToolKey?: string;
  /** Whether to allow tool selection (show Select button). Defaults to false. */
  allowToolSelection?: boolean;
}> = ({
  onSelect,
  className,
  toolsClassName,
  initialToolKey,
  allowToolSelection = false,
}) => {
  const { tools, providers } = useAvailableTools();

  const [query, setQuery] = useState('');
  /**
   * Fuzzy-searching the full catalog costs roughly 20ms per keystroke at a
   * thousand tools, which is enough to make the field feel like it is lagging
   * behind your typing. The input renders from `query` at normal priority while
   * the results recompute from this at a lower one, so keystrokes stay
   * immediate and the list catches up.
   */
  const deferredQuery = useDeferredValue(query);
  const [filter, setFilter] = useState<FilterMode>('ALL');
  const [useCaseId, setUseCaseId] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selection, setSelection] = useState<Selection | null>(() =>
    initialToolKey !== undefined
      ? {
          providerKey: getProviderFromToolKey(initialToolKey),
          toolKey: initialToolKey,
        }
      : null
  );

  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleToolSelect = useCallback(
    async (tool: AvailableTool) => {
      // Convert AvailableTool to the Tool type expected by onSelect
      // - key is used for both id and name (name is used by getToolDefinition)
      // - the display name goes to displayName
      await onSelect({
        id: tool.key,
        name: tool.key,
        displayName: tool.name,
        toolConnectionId: '',
        enabled: true,
        createdAt: new Date(),
      });
    },
    [onSelect]
  );

  const providerSearch = useFuzzySearch(
    providers.map(toProviderSearchRecord),
    PROVIDER_SEARCH_KEYS
  );

  const toolSearch = useFuzzySearch(
    tools.map(toToolSearchRecord),
    TOOL_SEARCH_KEYS
  );

  const useCase = useMemo(
    () => USE_CASES.find((uc) => uc.id === useCaseId) ?? null,
    [useCaseId]
  );

  // Resolve popular tool keys through the real catalog so providers whose keys
  // don't start with their own name still float to the top of their group.
  const popularProviders = useMemo(() => {
    const popular = new Set<string>(POPULAR_TOOL_KEYS);
    return new Set(
      tools.filter((t) => popular.has(t.key)).map((t) => t.extractedProvider)
    );
  }, [tools]);

  const catalog = useMemo(
    () =>
      buildCatalog({
        providers,
        query: deferredQuery,
        filter,
        useCase,
        providerMatchOrder:
          deferredQuery.length > 0
            ? providerSearch(deferredQuery).map((r) => r.provider)
            : [],
        toolMatchOrder:
          deferredQuery.length > 0
            ? toolSearch(deferredQuery).map((r) => r.key)
            : [],
        popularProviders,
      }),
    // `providerSearch` / `toolSearch` are rebuilt on every render by
    // `useFuzzySearch`; the memo keys off the inputs that actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providers, deferredQuery, filter, useCase, popularProviders]
  );

  // Land on something connected so the right pane opens with real content
  // instead of an empty prompt — but only when the caller didn't ask for a
  // specific tool, and only before the user has picked anything.
  const hasAutoSelected = useRef(selection !== null);
  if (!hasAutoSelected.current) {
    hasAutoSelected.current = true;
    const firstConnected = providers.find(
      (p) => p.configStatus === 'configured'
    );
    if (firstConnected !== undefined) {
      setSelection({ providerKey: firstConnected.provider });
    }
  }

  // A new result set invalidates the old cursor position. `KeyboardNavigableList`
  // clamps an out-of-range index, but a same-length result set needs this too.
  useEffect(() => {
    setSelectedIndex(0);
  }, [deferredQuery, filter, useCaseId]);

  const selectedProvider = useMemo(
    () =>
      selection === null
        ? null
        : providers.find((p) => p.provider === selection.providerKey) ?? null,
    [providers, selection]
  );

  const commit = useCallback(
    (index: number) => {
      const row = catalog.rows[index];
      if (row !== undefined) {
        setSelection(selectionFor(row));
      }
    },
    [catalog.rows]
  );

  const resetFilters = useCallback(() => {
    setFilter('ALL');
    setUseCaseId(null);
  }, []);

  /**
   * Escape peels back one layer at a time — query, then filters. Only once
   * there is nothing left to undo does it go unhandled and reach the modal
   * manager, which closes the dialog.
   *
   * Returns whether it consumed the key.
   */
  const handleEscape = useCallback((): boolean => {
    if (query.length > 0) {
      setQuery('');
      searchInputRef.current?.focus();
      return true;
    }
    if (filter !== 'ALL' || useCaseId !== null) {
      resetFilters();
      return true;
    }
    return false;
  }, [query, filter, useCaseId, resetFilters]);

  /**
   * Drives the list from the search field. Once focus is inside the list,
   * `KeyboardNavigableList` takes over — this stays bound to the input alone so
   * arrow keys remain text navigation in the right pane's credential fields.
   */
  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const count = catalog.rows.length;

      switch (event.key) {
        case 'ArrowDown':
          if (count === 0) {
            return;
          }
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % count);
          return;
        case 'ArrowUp':
          if (count === 0) {
            return;
          }
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + count) % count);
          return;
        case 'Enter':
          if (count === 0) {
            return;
          }
          event.preventDefault();
          commit(selectedIndex);
          return;
        case 'Escape':
          if (handleEscape()) {
            event.stopPropagation();
          }
          return;
      }
    },
    [catalog.rows.length, commit, selectedIndex, handleEscape]
  );

  const showDetail = selection !== null;

  return (
    <div className={cn('flex flex-col flex-1 min-h-0', className)}>
      <CatalogHeader
        onKeyDown={handleSearchKeyDown}
        inputRef={searchInputRef}
        activeDescendantId={
          catalog.rows[selectedIndex] !== undefined
            ? `tool-catalog-row-${selectedIndex}`
            : undefined
        }
        query={query}
        onQueryChange={setQuery}
        filter={filter}
        onFilterChange={setFilter}
        useCases={USE_CASES}
        selectedUseCaseId={useCaseId}
        onSelectUseCase={setUseCaseId}
        resultCount={catalog.rows.length}
      />

      <div className="flex flex-1 min-h-0 pt-3">
        <div
          className={cn(
            'flex-col min-h-0 w-full md:w-[19rem] lg:w-[21rem] shrink-0 md:border-r md:border-border/40 md:pr-3',
            toolsClassName,
            showDetail ? 'hidden md:flex' : 'flex'
          )}
        >
          {catalog.rows.length === 0 ? (
            <CatalogEmptyState
              query={deferredQuery}
              filter={filter}
              useCaseName={useCase?.name ?? null}
              onClearSearch={() => setQuery('')}
              onClearFilters={resetFilters}
            />
          ) : (
            <CatalogList
              groups={catalog.groups}
              rows={catalog.rows}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              onSelectItem={commit}
              onEscape={handleEscape}
              selectedRowId={rowIdFor(selection)}
            />
          )}
        </div>

        <div
          className={cn(
            'flex-1 min-w-0 min-h-0 md:pl-1',
            showDetail ? 'flex flex-col' : 'hidden md:flex md:flex-col'
          )}
        >
          {/* Sits outside the branch below so a selection that resolves to no
              provider still leaves a way back to the list on mobile. */}
          {showDetail && (
            <button
              type="button"
              className="md:hidden flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => setSelection(null)}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>All tools</span>
            </button>
          )}
          {selectedProvider === null ? (
            <DetailPlaceholder hasSelection={selection !== null} />
          ) : (
            <Suspense
              fallback={
                <div className="flex-1 flex items-center justify-center">
                  <Loading.Spinner />
                </div>
              }
            >
              <ProviderDetailPanel
                key={selectedProvider.provider}
                compact
                provider={selectedProvider}
                expandedToolKey={selection?.toolKey}
                onToolSelect={allowToolSelection ? handleToolSelect : undefined}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Right pane before anything is chosen. Also covers the rare case where a
 * remembered selection points at a provider the current filters removed.
 */
const DetailPlaceholder: React.FC<{ hasSelection: boolean }> = ({
  hasSelection,
}) => (
  <div className="flex flex-1 flex-col items-center justify-center text-center px-8">
    <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-muted/50 border border-border/50 mb-3">
      <Wrench className="h-5 w-5 text-muted-foreground/60" />
    </div>
    <p className="text-sm font-medium text-foreground">
      {hasSelection ? 'Provider unavailable' : 'Pick a provider'}
    </p>
    <p className="text-xs text-muted-foreground mt-1 max-w-[32ch]">
      {hasSelection
        ? "That provider isn't in the catalog anymore."
        : 'Select anything on the left to connect it and browse the tools it brings.'}
    </p>
  </div>
);

export const ToolSettingsModalPageRoot: React.FC<{
  onSelect: (tool: Tool & { isExisting?: boolean }) => Promise<void>;
}> = ({ onSelect }) => {
  return (
    <ModalPageView
      className="px-6 py-5 flex flex-col h-full max-w-none"
      title="Install Tool"
      subtitle="Search for tools to add to your Cotera instance."
    >
      <Suspense
        fallback={
          <div className="flex flex-col gap-3 mt-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        }
      >
        <ToolConnections onSelect={onSelect} allowToolSelection />
      </Suspense>
    </ModalPageView>
  );
};
